import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { ErrorStateComponent } from '../../../../../../../../../../_shared/components/states/error-state/error-state.component';
import { EmptyStateComponent } from '../../../../../../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorHandlerService } from '../../../../../../../../../../_shared/services/error-handler.service';
import { ChecklistTableComponent } from '../../../../../../../../_shared/components/checklist-table/checklist-table.component';
import { ChecklistTableActionEvent, ChecklistTableGroup, ChecklistTableSectionConfig } from '../../../../../../../../_shared/components/checklist-table/checklist-table.model';
import { DocumentViewModalComponent } from '../../../../../../../../_shared/components/modals/document-view-modal/document-view-modal.component';
import { VideoViewModalComponent } from '../../../../../../../../_shared/components/modals/video-view-modal/video-view-modal.component';
import { RoutineVideoCardsComponent } from '../../../../../../../../compliance-overview/components/routine-compliance-tasks/asset-routine-compliance-details/trip-checklist/routine-video-cards/routine-video-cards.component';
import {
  resolveInspectionTypeId,
  RoutineVideo,
  RoutineVideoKind,
  SubmissionVideoKind
} from '../../../../../../../../compliance-overview/models/routine-video.model';
import { ComplianceOverviewService } from '../../../../../../../../compliance-overview/services/compliance-overview.service';
import { ComplianceResultLabelKeys } from '../../../../../../../../_shared/enums/compliance-result.enum';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CHECKLIST_TYPE_NAME_KEYS, CHECKLIST_TYPES, ChecklistTypeIdEnum, getChecklistTypeOption } from '../../../../../../../../_shared/models/checklist-type.model';
import {
  ChecklistRequirementPhase,
  ChecklistRequirementResult,
  ChecklistRequirementResultAttachment,
  ChecklistRequirementResultRow,
  ChecklistRequirementResults,
  ChecklistRequirementResultsRequest
} from '../../../../../../../models/compliances.model';
import { CompliancesLibraryService } from '../../../../../../../services/compliances-library.service';

@Component({
  selector: 'app-checklist-requirement-results',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ErrorStateComponent,
    EmptyStateComponent,
    ChecklistTableComponent,
    RoutineVideoCardsComponent,
    TranslocoPipe
  ],
  templateUrl: './checklist-requirement-results.component.html',
  styleUrl: './checklist-requirement-results.component.scss'
})
export class ChecklistRequirementResultsComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private complianceLibraryService: CompliancesLibraryService = inject(CompliancesLibraryService);
  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private modalService: NgbModal = inject(NgbModal);
  private transloco: TranslocoService = inject(TranslocoService);
  private readonly cancelSubject = new Subject<void>();

  readonly checklistSections: ChecklistTableSectionConfig[] = [
    this.getChecklistSection(ChecklistTypeIdEnum.Document),
    this.getChecklistSection(ChecklistTypeIdEnum.Inspection)
  ];

  assetId: number | null = null;
  complianceId: number | null = null;
  taskId: number | null = null;
  assetName: string = '';
  complianceName: string = '';
  complianceType: string = '';

  isLoading: boolean = true;
  hasError: boolean = false;

  readonly phaseTabTitles: Record<ChecklistRequirementPhase, string> = {
    pre: this.transloco.translate('compliance.library.checklistResults.phaseTabs.pre'),
    post: this.transloco.translate('compliance.library.checklistResults.phaseTabs.post')
  };
  showTabs: boolean = false;
  activeTab: ChecklistRequirementPhase = 'pre';

  private allGroups: ChecklistTableGroup<ChecklistRequirementResultRow>[] = [];
  private preGroups: ChecklistTableGroup<ChecklistRequirementResultRow>[] = [];
  private postGroups: ChecklistTableGroup<ChecklistRequirementResultRow>[] = [];

  private routineTypeId: number | null = null;
  preVideos: RoutineVideo[] = [];
  postVideos: RoutineVideo[] = [];
  videosLoading: boolean = false;

  get checklistGroups(): ChecklistTableGroup<ChecklistRequirementResultRow>[] {
    if (!this.showTabs) {
      return this.allGroups;
    }
    return this.activeTab === 'pre' ? this.preGroups : this.postGroups;
  }

  get videos(): RoutineVideo[] {
    if (this.showTabs) {
      return this.activeTab === 'pre' ? this.preVideos : this.postVideos;
    }
    return [...this.preVideos, ...this.postVideos];
  }

  ngOnInit(): void {
    this.assetId = this.getNumberQueryParam('assetId');
    this.complianceId = this.getNumberQueryParam('complianceId');
    this.taskId = this.getNumberQueryParam('taskId');
    this.assetName = this.route.snapshot.queryParamMap.get('assetName') || '';
    this.complianceName = this.route.snapshot.queryParamMap.get('complianceName') || '';
    this.complianceType = this.route.snapshot.queryParamMap.get('complianceType') || '';

    if (!this.assetId || !this.complianceId || !this.taskId) {
      this.hasError = true;
      this.isLoading = false;
      return;
    }

    void this.loadResultsAsync();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  get hasNoResults(): boolean {
    return this.checklistGroups.length === 0;
  }

  onActionClicked(event: ChecklistTableActionEvent<ChecklistRequirementResultRow>): void {
    if (event.actionName !== 'view-document' || !event.row.documentUrl) {
      return;
    }

    const modalRef = this.modalService.open(DocumentViewModalComponent, {
      centered: true,
      size: 'lg'
    });

    const { checklistId, submissionId, attachmentReference } = event.row;
    modalRef.componentInstance.title = this.transloco.translate('compliance.library.checklistResults.attachment');
    modalRef.componentInstance.documentName = event.row.documentLabel;
    modalRef.componentInstance.documentUrl = event.row.documentUrl;

    if (submissionId != null && attachmentReference) {
      modalRef.componentInstance.downloadHandler = async () => {
        const response = await this.complianceOverviewService.getChecklistAttachmentDownloadLinkAsync(
          submissionId,
          checklistId,
          attachmentReference
        );
        return response.data ?? null;
      };
    }
  }

  onVideoClick(video: RoutineVideo): void {
    if (!video.videoUrl || this.taskId == null) {
      return;
    }

    const taskId = this.taskId;
    const modalRef = this.modalService.open(VideoViewModalComponent, {
      centered: true,
      size: 'lg'
    });

    modalRef.componentInstance.title = video.title;
    modalRef.componentInstance.videoName = video.title;
    modalRef.componentInstance.videoUrl = video.videoUrl;
    modalRef.componentInstance.downloadHandler = async () => {
      const response = await this.complianceOverviewService.getSubmissionVideoDownloadLinkAsync(
        taskId, video.inspectionTypeId, video.submissionVideoKind);
      return response.data ?? null;
    };
  }

  async loadResultsAsync(): Promise<void> {
    if (!this.assetId || !this.complianceId || !this.taskId) {
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.cancelSubject.next();

    try {
      const response = await this.complianceLibraryService.getChecklistRequirementResultsAsync(
        this.buildRequest(),
        this.cancelSubject
      );
      const results = response.data ?? [];

      this.applyPhaseGrouping(results);
      this.routineTypeId = results.find(group => group.routineTypeId != null)?.routineTypeId ?? null;
      this.isLoading = false;

      void this.loadVideosAsync(results);
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  toggleTab(tab: ChecklistRequirementPhase): void {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
    }
  }

  private async loadVideosAsync(results: ChecklistRequirementResults[]): Promise<void> {
    const routineTypeId = this.routineTypeId;
    if (routineTypeId == null || this.taskId == null) {
      return;
    }

    const phases = this.getPhasesToLoad(results);
    if (phases.length === 0) {
      return;
    }

    this.videosLoading = true;
    try {
      await Promise.all(phases.map(async phase => {
        const inspectionTypeId = resolveInspectionTypeId(routineTypeId, phase);
        if (inspectionTypeId == null) {
          return;
        }
        const videos = await this.loadPhaseVideosAsync(phase, inspectionTypeId);
        if (phase === 'pre') {
          this.preVideos = videos;
        } else {
          this.postVideos = videos;
        }
      }));
    } finally {
      this.videosLoading = false;
    }
  }

  private getPhasesToLoad(results: ChecklistRequirementResults[]): ChecklistRequirementPhase[] {
    const hasPhase = results.some(group => !!group.phase);
    if (hasPhase) {
      const phases = new Set(results.map(group => group.phase).filter((p): p is ChecklistRequirementPhase => !!p));
      return [...phases];
    }
        
    return (['pre', 'post'] as ChecklistRequirementPhase[])
      .filter(phase => resolveInspectionTypeId(this.routineTypeId, phase) != null);
  }

  private async loadPhaseVideosAsync(phase: ChecklistRequirementPhase, inspectionTypeId: number): Promise<RoutineVideo[]> {
    const taskId = this.taskId;
    if (taskId == null) {
      return [];
    }

    const phaseLabel = this.transloco.translate(
      phase === 'pre'
        ? 'compliance.shared.routineChecklist.legLabels.trip.pre'
        : 'compliance.shared.routineChecklist.legLabels.trip.post'
    );
    const kinds: { kind: RoutineVideoKind; title: string; videoKind: SubmissionVideoKind }[] = [
      { kind: 'external', title: this.transloco.translate('compliance.library.checklistResults.externalVideo'), videoKind: SubmissionVideoKind.Exterior },
      { kind: 'internal', title: this.transloco.translate('compliance.library.checklistResults.internalVideo'), videoKind: SubmissionVideoKind.Interior }
    ];

    return Promise.all(kinds.map(async ({ kind, title, videoKind }) => {
      const link = await this.complianceOverviewService
        .getSubmissionVideoLinkAsync(taskId, inspectionTypeId, videoKind)
        .catch(() => null);

      return {
        kind,
        title,
        phaseLabel,
        capturedAt: link?.data?.uploadedAt ?? '',
        videoUrl: link?.data?.url ?? '',
        inspectionTypeId,
        submissionVideoKind: videoKind
      } as RoutineVideo;
    }));
  }

  private applyPhaseGrouping(results: ChecklistRequirementResults[]): void {
    this.activeTab = 'pre';
    const hasPhase = results.some(group => !!group.phase);

    if (!hasPhase) {
      this.showTabs = false;
      this.allGroups = this.mapGroups(results);
      return;
    }

    this.preGroups = this.mapGroups(results.filter(group => group.phase === 'pre'));
    this.postGroups = this.mapGroups(results.filter(group => group.phase === 'post'));
    this.showTabs = this.preGroups.length > 0 && this.postGroups.length > 0;
    this.allGroups = this.showTabs ? [] : this.mapGroups(results);
  }

  private buildRequest(): ChecklistRequirementResultsRequest {
    return {
      taskId: this.taskId!,
      complianceId: this.complianceId!,
      assetId: this.assetId!
    };
  }

  private mapGroups(groups: ChecklistRequirementResults[]): ChecklistTableGroup<ChecklistRequirementResultRow>[] {
    return groups.map(group => ({
      id: group.checklistId,
      name: group.checklistName,
      type: this.getChecklistType(group.checklistType),
      rows: group.requirementResults.map(result => this.mapRequirementResult(result, group.checklistId))
    }));
  }

  private mapRequirementResult(result: ChecklistRequirementResult, checklistId: number): ChecklistRequirementResultRow {
    const firstAttachment = result.attachments?.[0];
    const documentLabel = firstAttachment
      ? this.buildDocumentLabel(firstAttachment)
      : this.transloco.translate('compliance.library.checklistResults.noDocument');
    const statusLabelKey = ComplianceResultLabelKeys[result.requirementResultStatus];

    return {
      ...result,
      notesLabel: result.notes?.trim() || this.transloco.translate('compliance.library.checklistResults.noNoteProvided'),
      requirementResultStatusLabel: statusLabelKey ? this.transloco.translate(statusLabelKey) : '',
      documentLabel,
      documentUrl: firstAttachment?.link ?? null,
      attachmentLabel: documentLabel,
      checklistId,
      attachmentReference: firstAttachment?.reference ?? null
    };
  }

  private buildDocumentLabel(attachment: ChecklistRequirementResultAttachment): string {
    const requirementNumber = (attachment.filename ?? '').split('-')[1] ?? '';
    const extension = attachment.extension ?? '';

    const uploadDate = attachment.uploadedAt?.split('T')[0] ?? '';
    const uploadTime = attachment.uploadedAt?.split('T')[1]?.slice(0, 5) ?? '';

    return `${requirementNumber}/${uploadDate}/${uploadTime}${extension}`;
  }

  private getChecklistType(checklistType: string | number): ChecklistTypeIdEnum {
    return getChecklistTypeOption(checklistType)?.id ?? ChecklistTypeIdEnum.Inspection;
  }

  private getChecklistSection(typeId: ChecklistTypeIdEnum): ChecklistTableSectionConfig {
    const checklistType = CHECKLIST_TYPES.find(type => type.id === typeId)!;

    return {
      type: checklistType.id,
      typeAliases: [checklistType.name],
      title: this.transloco.translate('compliance.library.checklistResults.sectionTitle', {
        type: this.transloco.translate(CHECKLIST_TYPE_NAME_KEYS[checklistType.id])
      }),
      columns: [
        { header: this.transloco.translate('compliance.library.checklistResults.columns.requirement'), value: 'requirementName', width: '2fr' },
        {
          header: this.transloco.translate(typeId === ChecklistTypeIdEnum.Document
            ? 'compliance.library.checklistResults.columns.linkedDocument'
            : 'compliance.library.checklistResults.columns.inspectionAttachment'),
          value: typeId === ChecklistTypeIdEnum.Document ? 'documentLabel' : 'attachmentLabel',
          type: 'link',
          linkUrlValue: 'documentUrl',
          actionName: 'view-document',
          actionLabel: this.transloco.translate('compliance.library.checklistResults.viewDocument'),
          width: '1.55fr'
        },
        { header: this.transloco.translate('compliance.library.checklistResults.columns.notes'), value: 'notesLabel', width: '1.55fr', mutedValue: this.transloco.translate('compliance.library.checklistResults.noNoteProvided') },
        {
          header: this.transloco.translate('compliance.library.checklistResults.columns.taskStatus'),
          value: 'requirementResultStatus',
          labelValue: 'requirementResultStatusLabel',
          type: 'status',
          width: '1fr'
        }
      ]
    };
  }

  private getNumberQueryParam(key: string): number | null {
    const value = Number(this.route.snapshot.queryParamMap.get(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  }
}
